import { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis } from 'recharts';
import { SPREAD_PRODUCTS, LOT_QUICK, SPREAD_QUICK, PROFIT_CURRENCIES } from '@/mocks/profit';
import { formatVN, Label, NumberInput, SelectInput, QuickChips, Stat } from '../ui';
import { NextStep } from './ProfitTab';

const SPREAD_ACCENT = '#7c3aed';
const REMAIN_COLOR = '#7c3aed';
const COST_COLOR = '#c4b5fd';

const WHEN_CARDS = [
  { icon: 'ri-price-tag-3-line', title: 'Ước tính chi phí một lệnh', desc: 'Nhập Spread, giá trị mỗi Pip và khối lượng để biết chi phí Spread của từng giao dịch.' },
  { icon: 'ri-crosshair-line', title: 'Xác định điểm hòa vốn', desc: 'Cộng Spread, Commission và Swap để biết giá cần di chuyển tối thiểu bao nhiêu Pip mới không lỗ.' },
  { icon: 'ri-pie-chart-line', title: 'Đánh giá tác động lên lợi nhuận', desc: 'Xem Spread chiếm bao nhiêu phần trăm trong lợi nhuận dự kiến qua biểu đồ tròn.' },
  { icon: 'ri-stack-line', title: 'Tính chi phí tích lũy', desc: 'Ước tính tổng chi phí Spread khi giao dịch nhiều lệnh trong nhiều ngày.' },
];

const tooltipStyle = {
  borderRadius: 12,
  border: '1px solid #ede9fe',
  fontSize: 12,
  boxShadow: '0 12px 30px -12px rgba(124,58,237,0.25)',
};

export default function SpreadTab({ onNext }: { onNext: () => void }) {
  const [product, setProduct] = useState('EURUSD');
  const [lots, setLots] = useState(1);
  const [spread, setSpread] = useState(1.5);
  const [pipValue, setPipValue] = useState(10);
  const [expectedProfit, setExpectedProfit] = useState(500);
  const [currency, setCurrency] = useState('USD');

  const [commission, setCommission] = useState(15);
  const [swap, setSwap] = useState(5);

  const [tradesPerDay, setTradesPerDay] = useState(5);
  const [days, setDays] = useState(20);

  const curr = useMemo(() => PROFIT_CURRENCIES.find((c) => c.value === currency) ?? PROFIT_CURRENCIES[0], [currency]);
  const rate = curr.rate;

  // Pip value, commission and swap are quoted in USD, so they are converted into
  // the account currency before any cost is computed.
  const pipValueAccount = pipValue * rate;
  const commissionAccount = commission * rate;
  const swapAccount = swap * rate;

  const spreadCost = spread * pipValueAccount * lots;
  const afterSpread = Math.max(expectedProfit - spreadCost, 0);
  const percent = expectedProfit > 0 ? (spreadCost / expectedProfit) * 100 : 0;
  const percentCapped = Math.min(percent, 100);

  const breakEvenTotal = spreadCost + commissionAccount + swapAccount;
  const breakEvenPips = pipValueAccount * lots > 0 ? breakEvenTotal / (pipValueAccount * lots) : 0;

  const totalCost = spreadCost * tradesPerDay * days;

  const selectProduct = (v: string) => {
    setProduct(v);
    const next = SPREAD_PRODUCTS.find((p) => p.value === v) ?? SPREAD_PRODUCTS[0];
    setPipValue(next.pipValue);
  };

  const donutData = [
    { name: 'Lợi nhuận còn lại', value: Math.max(afterSpread, 0.0001) },
    { name: 'Chi phí Spread', value: spreadCost },
  ];

  const cumulativeData = useMemo(() => {
    const arr = [];
    for (let d = 1; d <= days; d++) {
      arr.push({ day: d, cost: spreadCost * tradesPerDay * d });
    }
    return arr;
  }, [spreadCost, tradesPerDay, days]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left: inputs */}
        <div className="lg:col-span-3">
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
            <div className="mb-6 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                <i className="ri-percent-line text-lg" />
              </span>
              <h3 className="font-display text-lg font-bold text-slate-900">Thông tin giao dịch</h3>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label>Sản phẩm giao dịch</Label>
                  <SelectInput value={product} onChange={selectProduct} options={SPREAD_PRODUCTS.map((p) => ({ value: p.value, label: p.label }))} />
                </div>
                <div>
                  <Label>Khối lượng</Label>
                  <NumberInput value={lots} onChange={setLots} suffix="lot" min={0.01} step={0.01} />
                </div>
              </div>
              <div>
                <QuickChips options={LOT_QUICK} value={lots} onChange={setLots} />
              </div>

              <div>
                <Label hint={`${formatVN(spread, 1, 1)} Pip`}>Spread</Label>
                <NumberInput value={spread} onChange={setSpread} suffix="Pip" min={0} step={0.1} />
                <div className="mt-2.5">
                  <QuickChips options={SPREAD_QUICK} value={spread} onChange={setSpread} format={(v) => `${formatVN(v, 1, 1)}`} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label>Giá trị mỗi Pip</Label>
                  <NumberInput value={pipValue} onChange={setPipValue} suffix="USD" min={0} step={0.5} />
                </div>
                <div>
                  <Label>Tiền tệ tài khoản</Label>
                  <SelectInput value={currency} onChange={setCurrency} options={PROFIT_CURRENCIES.map((c) => ({ value: c.value, label: c.label }))} />
                </div>
              </div>

              <div>
                <Label>Lợi nhuận dự kiến</Label>
                <NumberInput value={expectedProfit} onChange={setExpectedProfit} suffix={curr.value} min={0} step={10} />
              </div>
            </div>
          </div>

          {/* Break-even */}
          <div className="mt-6 rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
            <h3 className="font-display text-lg font-bold text-slate-900">Điểm hòa vốn sau chi phí giao dịch</h3>
            <p className="mt-1 text-sm text-slate-500">Nhập các chi phí để biết giá cần di chuyển tối thiểu bao nhiêu Pip.</p>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div>
                <Label>Spread</Label>
                <NumberInput value={spread} onChange={setSpread} suffix="Pip" min={0} step={0.1} />
              </div>
              <div>
                <Label>Commission</Label>
                <NumberInput value={commission} onChange={setCommission} suffix="USD" min={0} step={1} />
              </div>
              <div>
                <Label>Swap</Label>
                <NumberInput value={swap} onChange={setSwap} suffix="USD" min={0} step={1} />
              </div>
            </div>
            <div className="mt-5 flex flex-col items-center gap-3 rounded-2xl border border-violet-100 bg-violet-50/50 p-6 text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-violet-600">Số Pip cần thiết để bù đắp chi phí</p>
              <p className="font-mono text-4xl font-bold tabular-nums text-slate-900">{formatVN(breakEvenPips, 1, 1)} Pip</p>
              <p className="max-w-md text-xs leading-relaxed text-slate-500">
                Giá cần di chuyển tối thiểu khoảng {formatVN(breakEvenPips, 1, 1)} Pip để bù đắp các chi phí giao dịch được nhập vào.
              </p>
            </div>
          </div>
        </div>

        {/* Right: diagnostic */}
        <div className="lg:col-span-2">
          <div className="flex h-full flex-col gap-6">
            <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-7">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-bold text-slate-900">Phân tích chi phí Spread</h3>
                <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-600">Diagnostic</span>
              </div>

              <div className="mt-4 text-center">
                <p className="font-mono text-5xl font-bold tabular-nums text-slate-900">{formatVN(spreadCost, 2, 2)} {curr.value}</p>
                <p className="mt-1 text-sm text-slate-500">Chi phí Spread</p>
              </div>

              {/* Donut */}
              <div className="relative mx-auto mt-6 h-56 w-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={72} outerRadius={96} paddingAngle={2} strokeWidth={0}>
                      <Cell fill={REMAIN_COLOR} />
                      <Cell fill={COST_COLOR} />
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-mono text-3xl font-bold tabular-nums text-slate-900">{formatVN(percentCapped, 1, 1)}%</span>
                  <span className="mt-1 max-w-[120px] text-center text-[10px] leading-tight text-slate-400">Lợi nhuận dự kiến bị Spread chiếm</span>
                </div>
              </div>

              <div className="mt-5 space-y-2.5">
                <Legend label="Lợi nhuận còn lại" value={`${formatVN(afterSpread, 2, 2)} ${curr.value}`} color={REMAIN_COLOR} />
                <Legend label="Chi phí Spread" value={`−${formatVN(spreadCost, 2, 2)} ${curr.value}`} color={COST_COLOR} />
              </div>

              <p className="mt-5 rounded-xl bg-slate-50 px-4 py-3 text-center text-sm font-medium text-slate-600">
                Spread đang chiếm <strong className="font-bold text-violet-700">{formatVN(percent, 1, 1)}%</strong> lợi nhuận dự kiến.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2.5">
              <Stat label="Lợi nhuận dự kiến" value={`${formatVN(expectedProfit, 0, 0)} ${curr.value}`} />
              <Stat label="Chi phí Spread" value={`−${formatVN(spreadCost, 0, 0)}`} />
              <Stat label="Sau Spread" value={`${formatVN(afterSpread, 0, 0)}`} accent={REMAIN_COLOR} />
            </div>
          </div>
        </div>
      </div>

      {/* Cumulative cost */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
        <h3 className="font-display text-lg font-bold text-slate-900">Spread tích lũy qua nhiều giao dịch</h3>
        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="space-y-5">
            <div>
              <Label hint="Tự động từ Spread × Pip × Lot">Chi phí Spread / giao dịch</Label>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <span className="font-mono text-base font-semibold text-slate-800">{formatVN(spreadCost, 2, 2)} {curr.value}</span>
                <span className="text-xs font-medium text-slate-400">tự động</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Số giao dịch / ngày</Label>
                <NumberInput value={tradesPerDay} onChange={setTradesPerDay} min={0} step={1} />
              </div>
              <div>
                <Label>Số ngày giao dịch</Label>
                <NumberInput value={days} onChange={setDays} min={1} step={1} />
              </div>
            </div>
            <div className="rounded-2xl border border-violet-100 bg-violet-50/50 p-5 text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-violet-600">Chi phí Spread ước tính</p>
              <p className="mt-2 font-mono text-4xl font-bold tabular-nums text-slate-900">{formatVN(totalCost, 0, 0)} {curr.value}</p>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cumulativeData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="costFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SPREAD_ACCENT} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={SPREAD_ACCENT} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="cost" name="Chi phí" stroke={SPREAD_ACCENT} strokeWidth={2.5} fill="url(#costFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* When to use */}
      <section>
        <h3 className="font-display text-lg font-bold text-slate-900">Khi nào nên sử dụng công cụ?</h3>
        <div className="mt-5 divide-y divide-slate-100 rounded-2xl border border-slate-200/80 bg-white px-6">
          {WHEN_CARDS.map((c) => (
            <div key={c.title} className="flex items-start gap-4 py-4">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <i className={`${c.icon} text-base`} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">{c.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <NextStep
        title="Bạn đã biết chi phí giao dịch."
        desc="Bước tiếp theo: mô phỏng tăng trưởng vốn."
        cta="Máy tính lợi nhuận kép"
        onClick={onNext}
      />
    </div>
  );
}

function Legend({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-xs text-slate-500">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
        {label}
      </span>
      <span className="font-mono text-sm font-semibold tabular-nums text-slate-700">{value}</span>
    </div>
  );
}